import React, { useState } from "react";
import { Link, useRoute } from "wouter";
import { Layout } from "@/components/layout";
import { Show, useUser } from "@clerk/react";
import {
  useGetCourse,
  useGetMyTeacherProfile,
  useListMyCourseStudents,
  useListMyCourseMessages,
  useBroadcastCourseMessage,
  useListMyStudentSnippets,
  useListMyStudentDirectMessages,
  useSendStudentDirectMessage,
  getGetCourseQueryKey,
  getGetMyTeacherProfileQueryKey,
  getListMyCourseStudentsQueryKey,
  getListMyCourseMessagesQueryKey,
  getListMyStudentSnippetsQueryKey,
  getListMyStudentDirectMessagesQueryKey,
  useListMyStudentSubmissions,
  getListMyStudentSubmissionsQueryKey,
  useGradeSubmission,
} from "@workspace/api-client-react";
import type { SubmissionWithContext } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft,
  Send,
  Mail,
  Users,
  MessageSquare,
  Code,
  ExternalLink,
  GraduationCap,
} from "lucide-react";
import { dateLongFmt } from "@/lib/format";
import { basePath } from "@/lib/clerk";

type Student = {
  id: number;
  studentName: string;
  studentEmail: string;
  gradeLevel: string;
  status: "active" | "refunded";
  refundedAt?: string | null;
  createdAt: string;
};

function StudentDialog({
  slug,
  student,
  open,
  onOpenChange,
}: {
  slug: string;
  student: Student;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const enrollmentId = student.id;
  const qc = useQueryClient();
  const { toast } = useToast();
  const [body, setBody] = useState("");

  const dmsQuery = useListMyStudentDirectMessages(slug, enrollmentId, {
    query: {
      enabled: open,
      queryKey: getListMyStudentDirectMessagesQueryKey(slug, enrollmentId),
    },
  });
  const snippetsQuery = useListMyStudentSnippets(slug, enrollmentId, {
    query: {
      enabled: open,
      queryKey: getListMyStudentSnippetsQueryKey(slug, enrollmentId),
    },
  });
  const sendDm = useSendStudentDirectMessage();
  const submissionsQuery = useListMyStudentSubmissions(slug, enrollmentId, {
    query: {
      enabled: open,
      queryKey: getListMyStudentSubmissionsQueryKey(slug, enrollmentId),
    },
  });

  const onSendDm = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await sendDm.mutateAsync({
        slug,
        enrollmentId,
        data: { body: trimmed },
      });
      qc.invalidateQueries({
        queryKey: getListMyStudentDirectMessagesQueryKey(slug, enrollmentId),
      });
      setBody("");
      toast({
        title: "Message sent",
        description: `Sent to ${student.studentName}.`,
      });
    } catch (err) {
      toast({
        title: "Couldn't send",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {student.studentName}
          </DialogTitle>
          <DialogDescription>
            {student.studentEmail} · Grade {student.gradeLevel}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="dm" className="mt-2">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="dm" className="gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Direct messages
            </TabsTrigger>
            <TabsTrigger value="code" className="gap-1.5">
              <Code className="w-3.5 h-3.5" /> Saved code
            </TabsTrigger>
            <TabsTrigger value="homework" className="gap-1.5">
              <GraduationCap className="w-3.5 h-3.5" /> Homework
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dm" className="space-y-4 mt-4">
            <form onSubmit={onSendDm} className="space-y-2">
              <Label htmlFor="dm-body" className="text-xs uppercase tracking-widest font-mono">
                New message
              </Label>
              <Textarea
                id="dm-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={`Hey ${student.studentName.split(" ")[0]}, just checking in…`}
                rows={3}
                maxLength={10000}
              />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  className="rounded-full"
                  disabled={!body.trim() || sendDm.isPending}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {sendDm.isPending ? "Sending…" : "Send"}
                </Button>
              </div>
            </form>

            <div className="border-t border-border pt-4">
              <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                // history
              </h4>
              {dmsQuery.isLoading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
              ) : dmsQuery.data && dmsQuery.data.length > 0 ? (
                <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {dmsQuery.data.map((m) => (
                    <li
                      key={m.id}
                      className="border border-border rounded-xl p-3 bg-background"
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <div className="text-xs font-medium">{m.fromTeacherName}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          {dateLongFmt.format(new Date(m.createdAt))}
                        </div>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  No messages yet.
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="code" className="mt-4">
            {snippetsQuery.isLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : snippetsQuery.data && snippetsQuery.data.length > 0 ? (
              <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {snippetsQuery.data.map((s) => (
                  <li
                    key={s.id}
                    className="border border-border rounded-xl p-3 bg-background flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{s.title}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">
                        {s.lesson
                          ? `Lesson ${String(s.lesson.orderIndex).padStart(2, "0")} · ${s.lesson.title}`
                          : "Standalone"}
                        {" · "}
                        {s.language}
                        {" · saved "}
                        {dateLongFmt.format(new Date(s.updatedAt))}
                      </div>
                    </div>
                    <Button asChild variant="outline" size="sm" className="rounded-full shrink-0">
                      <a href={`${basePath}/s/${s.id}`} target="_blank" rel="noopener noreferrer">
                        Open <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground italic">
                No saved code yet for this course.
              </div>
            )}
          </TabsContent>

          <TabsContent value="homework" className="mt-4">
            {submissionsQuery.isLoading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : submissionsQuery.data && submissionsQuery.data.length > 0 ? (
              <ul className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {submissionsQuery.data.map((sub) => (
                  <SubmissionRow
                    key={sub.id}
                    slug={slug}
                    enrollmentId={enrollmentId}
                    submission={sub}
                  />
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground italic">
                No homework turned in yet for this course.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionRow({
  slug,
  enrollmentId,
  submission,
}: {
  slug: string;
  enrollmentId: number;
  submission: SubmissionWithContext;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [grade, setGrade] = useState(submission.grade ?? "");
  const [feedback, setFeedback] = useState(submission.gradeFeedback ?? "");
  const gradeMutation = useGradeSubmission();

  const onGrade = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedGrade = grade.trim();
    if (!trimmedGrade) return;
    try {
      await gradeMutation.mutateAsync({
        id: submission.id,
        data: {
          grade: trimmedGrade,
          gradeFeedback: feedback.trim() || null,
        },
      });
      qc.invalidateQueries({
        queryKey: getListMyStudentSubmissionsQueryKey(slug, enrollmentId),
      });
      setEditing(false);
      toast({ title: "Grade sent", description: "The student will see it on the lesson page." });
    } catch (err) {
      toast({
        title: "Couldn't save grade",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const graded = submission.grade !== null;

  return (
    <li className="border border-border rounded-xl p-4 bg-background space-y-3">
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-medium truncate">{submission.homeworkTitle}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
            {dateLongFmt.format(new Date(submission.submittedAt))}
          </div>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">
          {submission.lessonTitle}
        </div>
      </div>
      <p className="text-sm whitespace-pre-wrap break-words bg-muted/40 border rounded-lg p-3 max-h-48 overflow-y-auto">
        {submission.content}
      </p>
      {graded && !editing ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
              Grade: <span className="text-foreground font-bold normal-case text-sm">{submission.grade}</span>
            </div>
            {submission.gradeFeedback && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {submission.gradeFeedback}
              </p>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full shrink-0"
            onClick={() => setEditing(true)}
          >
            Edit grade
          </Button>
        </div>
      ) : editing || !graded ? (
        <form onSubmit={onGrade} className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label htmlFor={`grade-${submission.id}`} className="text-xs">Grade</Label>
              <Input
                id={`grade-${submission.id}`}
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder="A, 9/10, ✓…"
                maxLength={20}
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor={`feedback-${submission.id}`} className="text-xs">Feedback (optional)</Label>
              <Input
                id={`feedback-${submission.id}`}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Nice work! Next time try…"
                maxLength={5000}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              className="rounded-full"
              disabled={!grade.trim() || gradeMutation.isPending}
            >
              {gradeMutation.isPending ? "Saving…" : graded ? "Update grade" : "Send grade"}
            </Button>
            {editing && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="rounded-full"
                onClick={() => {
                  setGrade(submission.grade ?? "");
                  setFeedback(submission.gradeFeedback ?? "");
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      ) : null}
    </li>
  );
}

function StudentsAndMessages({ slug }: { slug: string }) {
  const { data: course } = useGetCourse(slug, {
    query: { enabled: !!slug, queryKey: getGetCourseQueryKey(slug) },
  });
  const { data: students, isLoading: studentsLoading } = useListMyCourseStudents(slug, {
    query: { enabled: !!slug, queryKey: getListMyCourseStudentsQueryKey(slug) },
  });
  const { data: messages } = useListMyCourseMessages(slug, {
    query: { enabled: !!slug, queryKey: getListMyCourseMessagesQueryKey(slug) },
  });
  const broadcast = useBroadcastCourseMessage();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [openStudent, setOpenStudent] = useState<Student | null>(null);

  // Refunded students stay in the roster for history but the broadcast
  // backend already filters to active enrollments; surface the active
  // count separately so teachers see the real reach of their broadcast.
  const activeStudents = students?.filter((s) => s.status === "active") ?? [];
  const refundedCount = (students?.length ?? 0) - activeStudents.length;

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await broadcast.mutateAsync({ slug, data: { subject: subject.trim(), body: body.trim() } });
      qc.invalidateQueries({ queryKey: getListMyCourseMessagesQueryKey(slug) });
      setSubject("");
      setBody("");
      toast({
        title: "Message sent",
        description: `Broadcast to ${activeStudents.length} active student${activeStudents.length === 1 ? "" : "s"}.`,
      });
    } catch (err) {
      toast({
        title: "Couldn't send",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 md:py-16 max-w-5xl">
      <div className="mb-8">
        <Button asChild variant="ghost" size="sm" className="rounded-full -ml-2 mb-4">
          <Link href="/teacher">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to portal
          </Link>
        </Button>
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
          // students &amp; messages
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          {course?.title ?? "Course"}
        </h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <section className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-display text-xl font-bold">
              Roster ({activeStudents.length} active
              {refundedCount > 0 ? ` · ${refundedCount} refunded` : ""})
            </h2>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Click a student to DM them or peek at their saved code.
          </p>
          <div className="bg-card border border-border rounded-2xl divide-y divide-border max-h-[480px] overflow-y-auto">
            {studentsLoading && (
              <div className="px-5 py-6 text-sm text-muted-foreground">Loading…</div>
            )}
            {students && students.length === 0 && (
              <div className="px-5 py-6 text-sm text-muted-foreground">
                No enrollments yet.
              </div>
            )}
            {students?.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setOpenStudent(s as Student)}
                className="w-full text-left px-5 py-3.5 hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`font-medium truncate ${s.status === "refunded" ? "text-muted-foreground line-through" : ""}`}>
                    {s.studentName}
                  </div>
                  {s.status === "refunded" && (
                    <span className="shrink-0 inline-flex items-center rounded-full border border-destructive/30 bg-destructive/10 text-destructive px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest">
                      Refunded
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">{s.studentEmail}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  Grade {s.gradeLevel} · joined {dateLongFmt.format(new Date(s.createdAt))}
                  {s.status === "refunded" && s.refundedAt
                    ? ` · refunded ${dateLongFmt.format(new Date(s.refundedAt))}`
                    : ""}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="lg:col-span-3 space-y-8">
          <form
            id="message"
            onSubmit={onSend}
            className="bg-card border border-border rounded-2xl p-6 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-4 h-4" aria-hidden="true" />
              <h2 className="font-display text-xl font-bold">Broadcast a message</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Sent to all enrolled students. They'll see it in their classroom and <em>My classes</em> portal.
            </p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="m-subject">Subject</Label>
                <Input
                  id="m-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Heads up about Tuesday's session…"
                  required
                  maxLength={200}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="m-body">Message</Label>
                <Textarea
                  id="m-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Hi everyone, just a reminder that…"
                  rows={5}
                  required
                  maxLength={10000}
                  className="mt-1.5"
                />
              </div>
              <Button
                type="submit"
                className="rounded-full"
                disabled={!subject.trim() || !body.trim() || broadcast.isPending}
              >
                <Send className="w-4 h-4 mr-1.5" />
                {broadcast.isPending ? "Sending…" : "Send to roster"}
              </Button>
            </div>
          </form>

          <div>
            <h3 className="font-display text-lg font-bold mb-3">Recent messages</h3>
            {messages && messages.length === 0 && (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-6 text-center">
                No messages sent yet.
              </div>
            )}
            <ul className="space-y-3">
              {messages?.map((m) => (
                <li key={m.id} className="border border-border rounded-xl p-4 bg-background">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-medium">{m.subject}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {dateLongFmt.format(new Date(m.createdAt))}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{m.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      {openStudent && (
        <StudentDialog
          slug={slug}
          student={openStudent}
          open={!!openStudent}
          onOpenChange={(next) => {
            if (!next) setOpenStudent(null);
          }}
        />
      )}
    </div>
  );
}

export default function TeacherCourseStudents() {
  const [, params] = useRoute("/teacher/courses/:slug/students");
  const slug = params?.slug || "";
  const { isLoaded } = useUser();
  const profileQuery = useGetMyTeacherProfile({
    query: { retry: false, queryKey: getGetMyTeacherProfileQueryKey() },
  });

  return (
    <Layout>
      <Show when="signed-out">
        <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
          <p className="text-muted-foreground mb-6">Sign in to access the teacher portal.</p>
          <Button asChild className="rounded-full">
            <Link href={`/sign-in?redirect_url=${encodeURIComponent(`${basePath}/teacher`)}`}>Sign in</Link>
          </Button>
        </div>
      </Show>
      <Show when="signed-in">
        {!isLoaded || profileQuery.isLoading ? (
          <div className="container mx-auto px-4 py-24 text-center text-muted-foreground" role="status">
            Loading…
          </div>
        ) : profileQuery.isError || !profileQuery.data ? (
          <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
            <p className="text-muted-foreground mb-6">You need a teacher profile first.</p>
            <Button asChild className="rounded-full">
              <Link href="/teacher">Open teacher portal</Link>
            </Button>
          </div>
        ) : (
          <StudentsAndMessages slug={slug} />
        )}
      </Show>
    </Layout>
  );
}
